import React, { useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
    Typography,
    Button,
    TextField,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Card,
    CardContent,
    Box,
    Stack,
    IconButton,
    Tooltip,
    Fade,
    CircularProgress,
    Snackbar,
    Alert,
    Divider,
    Container
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import { AddCircle, Cancel, Close, Save } from '@mui/icons-material';


const theme = createTheme({
    palette: {
        primary: {
            main: '#1976d2',
        },
        secondary: {
            main: '#dc004e',
        },
        background: {
            default: '#f5f5f5',
        },
    },
    typography: {
        h4: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 500,
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    textTransform: 'none',
                    fontWeight: 600,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                },
            },
        },
    },
});

// Styled components
const Root = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    maxWidth: '100%',
    margin: '0 auto',
}));

const Header = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
}));

const FormField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const ActionButton = styled(Button)(({ theme }) => ({
    height: 48,
    fontWeight: 600,
    boxShadow: theme.shadows[2],
}));

const LocationManagement = () => {
    // States
    const [view, setView] = useState('table');
    const [locations, setLocations] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    // Form states for create/edit
    const [formData, setFormData] = useState({
        name: '',
        address: '',
        country: '',
        state: '',
        city: '',
        pincode: '',
        latitude: '',
        longitude: ''
    });

    // Load locations on component mount
    useEffect(() => {
        fetchLocations();
    }, []);

    // Fetch all locations
    const fetchLocations = async () => {
        setLoading(true);
        try {
            const response = await fetch(`http://biometick.com/get-locations/${localStorage.getItem("userID")}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const data = await response.json();
            if (Array.isArray(data) && data.length > 0) {
                setLocations(data);
            }
        } catch (error) {
            showNotification('Failed to load locations', 'error');
            console.error('Error fetching locations:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    // Reset form data
    const resetForm = () => {
        setFormData({
            name: '',
            address: '',
            country: '',
            state: '',
            city: '',
            pincode: '',
            latitude: '',
            longitude: ''
        });
    };

    // Show view for creating new location
    const showCreateView = () => {
        resetForm();
        setView('create');
    };

    // Show view for editing location
    const showEditView = (location) => {
        setSelectedItem(location);
        setFormData({
            name: location.name || '',
            address: location.address || '',
            country: location.country || '',
            state: location.state || '',
            city: location.city || '',
            pincode: location.pincode || '',
            latitude: location.latitude || '',
            longitude: location.longitude || ''
        });
        setView('edit');
    };

    // Show notification
    const showNotification = (message, severity = 'success') => {
        setNotification({
            open: true,
            message,
            severity
        });
    };

    // Close notification
    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    // Handle create new location
    const handleCreateLocation = async () => {
        setLoading(true);
        try {
            const myHeaders = new Headers();
            myHeaders.append("Authorization", `Bearer ${localStorage.getItem("token")}`);
            myHeaders.append("Content-Type", "application/x-www-form-urlencoded");

            const urlencoded = new URLSearchParams();
            urlencoded.append("name", formData.name);
            urlencoded.append("address", formData.address);
            urlencoded.append("country", formData.country);
            urlencoded.append("userID", localStorage.getItem("userID"));
            urlencoded.append("state", formData.state);
            urlencoded.append("city", formData.city);
            urlencoded.append("pincode", formData.pincode);
            urlencoded.append("latitude", formData.latitude);
            urlencoded.append("longitude", formData.longitude);

            const response = await fetch("http://biometick.com/post-locations", {
                method: 'POST',
                headers: myHeaders,
                body: urlencoded,
                redirect: 'follow'
            });

            const result = await response.json();

            if (result && result._id) {
                showNotification('Location created successfully');
                setView('table');
                fetchLocations();
            } else {
                showNotification('Failed to create location', 'error');
            }
        } catch (error) {
            showNotification('An error occurred', 'error');
            console.error('Error creating location:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle update location
    const handleUpdateLocation = async () => {
        setLoading(true);
        try {
            const response = await fetch(`http://biometick.com/modify-locations/${selectedItem._id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(formData)
            });

            const result = await response.json();

            if (result && result._id) {
                showNotification('Location updated successfully');
                setView('table');
                fetchLocations();
            } else {
                showNotification('Failed to update location', 'error');
            }
        } catch (error) {
            showNotification('An error occurred', 'error');
            console.error('Error updating location:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle delete location
    const handleDeleteLocation = async (id) => {
        if (!window.confirm('Are you sure you want to delete this location?')) {
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`http://biometick.com/delete-locations/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                setLocations(locations.filter(location => location._id !== id));
                showNotification('Location deleted successfully');
            } else {
                showNotification('Failed to delete location', 'error');
            }
        } catch (error) {
            showNotification('An error occurred', 'error');
            console.error('Error deleting location:', error);
        } finally {
            setLoading(false);
        }
    };

    // Render location table
    const renderLocationTable = () => (<TableContainer component={Paper} elevation={2} sx={{ mt: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Table aria-label="department table">
            <TableHead>
                <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>Pincode</TableCell>
                    <TableCell>Latitude</TableCell>
                    <TableCell>Longitude</TableCell>
                    <TableCell align="center">Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {locations.length > 0 ? (
                    locations.map((location) => (
                        <TableRow key={location._id} hover>
                            <TableCell>{location.name}</TableCell>
                            <TableCell>{location.address}</TableCell>
                            <TableCell>{location.country}</TableCell>
                            <TableCell>{location.state}</TableCell>
                            <TableCell>{location.city}</TableCell>
                            <TableCell>{location.pincode}</TableCell>
                            <TableCell>{location.latitude}</TableCell>
                            <TableCell>{location.longitude}</TableCell>
                            <TableCell align="center">
                                <Box>
                                    <Tooltip title="Edit" arrow>
                                        <IconButton
                                            color="primary"
                                            onClick={() => showEditView(location)}
                                            size="small"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete" arrow>
                                        <IconButton
                                            color="error"
                                            onClick={() => handleDeleteLocation(location._id)}
                                            size="small"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))
                ) : (
                    <TableRow>
                        <TableCell colSpan={9} align="center">
                            <Typography variant="body1" color="textSecondary" sx={{ py: 3 }}>
                                No locations found. Click "Add Location" to create one.
                            </Typography>
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    </TableContainer>
    );

    // Render location form (create or edit)
    const renderLocationForm = () => {
        const isEditMode = view === 'edit';

        return (
            <Card elevation={3}>
                <CardContent>
                    <Stack spacing={2}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormField
                                name="name"
                                label="Location Name"
                                variant="outlined"
                                fullWidth
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                            <FormField
                                name="address"
                                label="Address"
                                variant="outlined"
                                fullWidth
                                value={formData.address}
                                onChange={handleInputChange}
                                required
                            />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormField
                                name="country"
                                label="Country"
                                variant="outlined"
                                fullWidth
                                value={formData.country}
                                onChange={handleInputChange}
                                required
                            />
                            <FormField
                                name="state"
                                label="State/Province"
                                variant="outlined"
                                fullWidth
                                value={formData.state}
                                onChange={handleInputChange}
                                required
                            />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormField
                                name="city"
                                label="City"
                                variant="outlined"
                                fullWidth
                                value={formData.city}
                                onChange={handleInputChange}
                                required
                            />
                            <FormField
                                name="pincode"
                                label="Postal/Zip Code"
                                variant="outlined"
                                fullWidth
                                value={formData.pincode}
                                onChange={handleInputChange}
                                required
                            />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormField
                                name="latitude"
                                label="Latitude"
                                variant="outlined"
                                fullWidth
                                value={formData.latitude}
                                onChange={handleInputChange}
                                type="number"
                                inputProps={{ step: "0.000001" }}
                            />
                            <FormField
                                name="longitude"
                                label="Longitude"
                                variant="outlined"
                                fullWidth
                                value={formData.longitude}
                                onChange={handleInputChange}
                                type="number"
                                inputProps={{ step: "0.000001" }}
                            />
                        </Box>
                        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<Cancel />}
                                onClick={() => setView('table')}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={isEditMode ? <Save /> : <AddCircle />}
                                onClick={isEditMode ? handleUpdateLocation : handleCreateLocation}
                                color="primary"
                            >
                                {isEditMode ? 'Update Location' : 'Create Location'}
                            </Button>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
        );
    };

    return (<ThemeProvider theme={theme}>
        <Container maxWidth="lg" sx={{ py: 2 }}>
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h4" color="primary">
                        {view === 'table' && 'Locations'}
                        {view === 'create' && 'Create Locations'}
                        {view === 'edit' && 'Edit Locations'}
                    </Typography>

                    {view === 'table' ? <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={showCreateView}
                    >
                        Add Locations
                    </Button> : <Button
                        variant="contained"
                        color="warning"
                        startIcon={<Close />}
                        onClick={() => setView('table')}>
                        Cancel
                    </Button>}
                </Box>
                <Divider sx={{ mt: 2 }} />
            </Box>
            {view === 'table' && renderLocationTable()}
            {(view === 'create' || view === 'edit') && renderLocationForm()}

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                TransitionComponent={Fade}
            >
                <Alert
                    onClose={handleCloseNotification}
                    severity={notification.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Container>
    </ThemeProvider>);
};

export default LocationManagement;